import type { JSX } from 'react';
import { Store, useStore } from 'dg-web-shared/lib/Flux.tsx';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import {
    ConfirmSaveHeader,
    HalfSlideIn,
    LoaderHeader,
    SlideInBody,
} from '../../ui/slidein/Slidein.tsx';
import * as CustomerClearanceState from '../state/CustomerClearanceState.ts';
import * as PermitTypeState from '../../common/state/PermitTypeState.ts';
import { isDefined, thenElse } from 'dg-web-shared/lib/MaybeV2.ts';
import { SingleSelection } from '../../ui/labeled-elements/SingleSelection.tsx';
import { Translation } from '../../common/i18n/Text.ts';
import * as CustomerClearanceTexts from '../i18n/CustomerClearanceTexts.ts';
import { TextField } from '../../ui/labeled-elements/TextField.tsx';
import * as AsyncRequest from '../../AsyncRequest.ts';
import * as Http from '../../api/Http.ts';
import { Response } from 'dg-web-shared/lib/HttpResponse.ts';

export interface CustomerClearanceCreateSlideInTexts {
    slideInHeader: Translation;
    permitTypes: Translation;
    customerNr: Translation;
    remark: Translation;

    createInProgress: Translation;

    invalidCustomerNr: Translation;
    noPermitTypesSelected: Translation;
    hasOverlaps: Translation;
}

export const closeCreate = (s: Store): string => {
    CustomerClearanceState.Add.reset(s);
    CustomerClearanceState.AddResponse.reset(s);
    CustomerClearanceState.AddErrors.reset(s);
    return 'CustomerClearanceCreateSlideIncloseCreate';
};

const createClearance = AsyncRequest.request(
    Http.OperatorAccount.CustomerClearance.create,
    (store: Store, res: Response): string => {
        CustomerClearanceState.AddResponse.setResponse(store, res);
        if (res.statusCode.cls.success) {
            closeCreate(store);
            CustomerClearanceState.List.reset(store);
        } else if (res.statusCode.cls.error) {
            CustomerClearanceState.AddErrors.reset(store);
            CustomerClearanceState.AddErrors.stateWrite(store, {
                errors: res.body.errors,
            });
        }
        return 'CustomerClearanceCreateSlideIncreateClearance';
    },
);

export function CustomerClearanceCreateSlideIn() {
    const { storeState, update, store } = useStore(store => ({
        add: CustomerClearanceState.Add.get(store),
        addResponse: CustomerClearanceState.AddResponse.get(store),
        addErrors: CustomerClearanceState.AddErrors.get(store),
    }));
    const { language } = useLanguage();

    const texts =
        CustomerClearanceTexts.customerClearanceCreateSlideInTexts[language];

    function isFormValid(): boolean {
        return CustomerClearanceState.Add.isValid(storeState.add);
    }

    function renderHeader(): JSX.Element {
        if (storeState.addResponse.pending) {
            return <LoaderHeader title={texts.createInProgress()} />;
        } else {
            return (
                <ConfirmSaveHeader
                    language={language}
                    title={texts.slideInHeader()}
                    onCancel={() => update(closeCreate)}
                    onSave={() => {
                        if (isFormValid()) {
                            update(createClearance, {
                                customerNr: parseInt(
                                    storeState.add.customerNr!,
                                    10,
                                ),
                                remark: storeState.add.remark,
                                permitTypeIds:
                                    storeState.add.permitTypeIds || [],
                            });
                        } else {
                            update(store =>
                                CustomerClearanceState.Add.stateWrite(store, {
                                    confirmPressedWhileError: true,
                                }),
                            );
                        }
                    }}
                />
            );
        }
    }

    function renderSelectedPermitTypes(): JSX.Element | null {
        if (
            !storeState.add.permitTypeIds ||
            storeState.add.permitTypeIds.length === 0
        ) {
            return null;
        } else {
            return (
                <div>
                    {storeState.add.permitTypeIds.map((id: number) => {
                        const type = PermitTypeState.getById(store, id);
                        if (isDefined(type)) {
                            return <div key={type.id}>{type.description}</div>;
                        } else {
                            return null;
                        }
                    })}
                </div>
            );
        }
    }

    function customerNrErrorText(): string {
        return (storeState.add.confirmPressedWhileError &&
            !CustomerClearanceState.Add.isCustomerNrValid(storeState.add)) ||
            CustomerClearanceState.AddErrors.customerNrInvalid(
                storeState.addErrors,
            )
            ? texts.invalidCustomerNr()
            : '';
    }

    function permitTypesErrorText(): string {
        return storeState.add.confirmPressedWhileError &&
            !CustomerClearanceState.Add.isPermitTypeIdsValid(storeState.add)
            ? texts.noPermitTypesSelected()
            : CustomerClearanceState.AddErrors.hasOverlaps(storeState.addErrors)
              ? texts.hasOverlaps()
              : '';
    }

    function renderBody(): JSX.Element {
        return (
            <SlideInBody>
                <TextField
                    inputType="text"
                    label={texts.customerNr()}
                    onChange={(v: string) =>
                        update(store =>
                            CustomerClearanceState.Add.stateWrite(store, {
                                customerNr: v,
                            }),
                        )
                    }
                    value={thenElse(storeState.add.customerNr, cnr => cnr, '')}
                    errorText={customerNrErrorText()}
                />
                <TextField
                    inputType="text"
                    label={texts.remark()}
                    onChange={(v: string) =>
                        update(store =>
                            CustomerClearanceState.Add.stateWrite(store, {
                                remark: v,
                            }),
                        )
                    }
                    value={thenElse(storeState.add.remark, r => r, '')}
                    maxLength={20}
                />
                <SingleSelection
                    label={texts.permitTypes()}
                    focused={false}
                    selection={renderSelectedPermitTypes()}
                    onClick={() =>
                        update(store =>
                            CustomerClearanceState.Add.stateWrite(store, {
                                permitTypeSelectionOpen: true,
                            }),
                        )
                    }
                    onClear={null}
                    errorText={permitTypesErrorText()}
                />
            </SlideInBody>
        );
    }

    return (
        <HalfSlideIn open={!!storeState.add.createCustomerClearanceOpen}>
            {renderBody()}
            {renderHeader()}
        </HalfSlideIn>
    );
}
